import { Component, inject, NgModule, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CustomModalOptions,
  ModalAbstractComponent,
  ModalService
} from '@bling-fe/shared/modals/base-modal';
import {
  ConfirmModalComponent,
  ConfirmModalComponentProps
} from '@bling-fe/shared/modals/confirm-modal';
import { Optional } from '../../shared/services/util.service';
import { ArticleType } from '../../core/declarations/article.interface';
import { ReportType } from '../../core/declarations/report.interface';
import {
  ConfirmWithReasonModalComponent,
  ConfirmWithReasonModalResult
} from '../confirm-with-reason-modal/confirm-with-reason-modal.component';
import {
  EventDeletionModalComponent,
  EventDeletionModalResult
} from '../event-deletion-modal/event-deletion-modal.component';
import { ShareService } from '../../shared/services/share.service';
import { IonicModule } from '@ionic/angular';
import { marker as _ } from '@bling-fe/shared/utils';
import { TranslationModule } from '../../core/translation/translation.module';
import { SvgIconComponent } from '@ngneat/svg-icon';

type DeleteModalProps = Optional<
  ConfirmModalComponentProps,
  'cancelLabel' | 'confirmLabel' | 'confirmColor'
>;

type ReportModalProps = Optional<
  ConfirmModalComponentProps,
  'cancelLabel' | 'confirmLabel' | 'confirmColor' | 'description'
>;
type ActionLabels = Partial<Record<OptionsModalAction, string>>;

export type OptionsModalAction =
  | 'edit'
  | 'move'
  | 'share'
  | 'manage'
  | 'delete'
  | 'deleteWithReason'
  | 'flag'
  | 'calendar'
  | 'block'
  | 'activate'
  | 'duplicate'
  | 'export'
  | 'copyRegistrationLink';

export interface OptionsModalProps {
  title?: string;
  deleteModalProps?: DeleteModalProps;
  flagModalProps?: ReportModalProps;
  blockModalProps?: ReportModalProps;
  actionsEnabled?: OptionsModalAction[];
  actionLabels?: ActionLabels;
  isAdmin?: boolean;
  isEventOwner?: boolean;
  isEvent?: boolean;
}

export type OptionsModalDismissType =
  | DismissValue
  | DismissActionWithReason
  | false;

type DismissValue = {
  action: Exclude<OptionsModalAction, 'deleteWithReason' | 'flag' | 'block'>;
};

type DismissActionWithReason = {
  reason_for_action: string;
  action: 'deleteWithReason' | 'flag' | 'block';
};

export type OptionsModalResult = { action: OptionsModalAction } | true;

@NgModule({})
export class SharedModalsUiOptionsModalIconsModule {}

@Component({
  selector: 'fl-shared-modals-ui-options-modal',
  imports: [
    CommonModule,
    IonicModule,
    TranslationModule,
    SvgIconComponent,
    SharedModalsUiOptionsModalIconsModule
  ],
  templateUrl: './shared-modals-ui-options-modal.component.html',
  styleUrls: ['./shared-modals-ui-options-modal.component.scss'],
  providers: [
    {
      provide: ModalAbstractComponent,
      useExisting: SharedModalsUiOptionsModalComponent
    }
  ]
})
export class SharedModalsUiOptionsModalComponent
  extends ModalAbstractComponent<OptionsModalProps, OptionsModalDismissType>
  implements OnInit
{
  public static override modalOptions: CustomModalOptions = {
    cssClass: 'fl-options auto-height bottom-sheet-modal',
    id: 'OPTIONS'
  };

  hasDivider = false;

  shareEnabled = false;
  private modalService = inject(ModalService);

  public ArticleType = ArticleType;

  constructor() {
    super();
  }

  async ngOnInit() {
    this.props.title ||= _('MODALS.OPTIONS.TITLE');
    this.shareEnabled = await ShareService.enabled;
    this.handleDivider();
  }

  async handleDelete() {
    const shouldDelete = await this.confirmDelete();
    this.dismiss(shouldDelete ? { action: 'delete' } : false, null, 'OPTIONS');
  }

  async handleDeleteWithReason() {
    // Use enhanced event deletion modal for events, simple modal for other items
    if (this.props.isEvent) {
      const shouldDelete = await this.confirmEventDeletionWithReasons();
      this.dismiss(
        shouldDelete?.finishAction
          ? {
              action: 'deleteWithReason',
              reason_for_action: JSON.stringify({
                reason_for_deletion: shouldDelete.reason_for_deletion,
                cancellation_reason: shouldDelete.cancellation_reason
              })
            }
          : false,
        null,
        'OPTIONS'
      );
    } else {
      const shouldDelete = await this.confirmDeleteWithReason();
      this.dismiss(
        shouldDelete?.finishAction
          ? {
              action: 'deleteWithReason',
              reason_for_action: shouldDelete.modalMessage
            }
          : false,
        null,
        'OPTIONS'
      );
    }
  }

  async handleReportWithReason(action: ReportType) {
    const shouldReport =
      action === 'flag'
        ? await this.confirmReportWithReason()
        : await this.confirmBlockWithReason();
    this.dismiss(
      shouldReport?.finishAction
        ? {
            action: action,
            reason_for_action: shouldReport.modalMessage
          }
        : false,
      null,
      'OPTIONS'
    );
  }

  public handleAction(action: DismissValue['action']) {
    this.dismiss({ action: action });
  }

  private handleDivider() {
    this.hasDivider =
      ((this.props.actionsEnabled?.includes('flag') ||
        this.props.actionsEnabled?.includes('block')) &&
        (this.props.actionsEnabled?.includes('delete') ||
          this.props.actionsEnabled?.includes('deleteWithReason') ||
          this.props.actionsEnabled?.includes('edit') ||
          this.props.actionsEnabled?.includes('duplicate') ||
          this.props.actionsEnabled?.includes('share') ||
          this.props.actionsEnabled?.includes('calendar'))) ||
      false;
  }

  private async confirmDelete(): Promise<boolean> {
    const confirmed = await this.modalService.presentAndGetResult(
      ConfirmModalComponent,
      {
        cancelLabel:
          this.props.deleteModalProps?.cancelLabel ?? _('COMMON.LABELS.CANCEL'),
        confirmLabel:
          this.props.deleteModalProps?.confirmLabel ??
          _('COMMON.LABELS.DELETE'),
        confirmColor: 'danger',
        title:
          this.props.deleteModalProps?.title ?? _('COMMON.LABELS.DELETE_TITLE'),
        description:
          this.props.deleteModalProps?.description ??
          _('COMMON.LABELS.DELETE_DESCRIPTION')
      }
    );

    return !!confirmed;
  }

  private async confirmDeleteWithReason(): Promise<ConfirmWithReasonModalResult> {
    return await this.modalService.presentAndGetResult(
      ConfirmWithReasonModalComponent,
      {
        cancelLabel:
          this.props.deleteModalProps?.cancelLabel ?? _('COMMON.LABELS.CANCEL'),
        confirmLabel:
          this.props.deleteModalProps?.confirmLabel ??
          _('COMMON.LABELS.DELETE'),
        confirmColor: 'danger',
        title: this.props.deleteModalProps?.title ?? _('COMMON.LABELS.DELETE'),
        description:
          this.props.deleteModalProps?.description ??
          _('COMMON.LABELS.DELETE_DESCRIPTION')
      }
    );
  }

  private async confirmEventDeletionWithReasons(): Promise<EventDeletionModalResult> {
    return await this.modalService.presentAndGetResult(
      EventDeletionModalComponent,
      {
        cancelLabel: _('COMMON.LABELS.CANCEL'),
        confirmLabel: _('COMMON.LABELS.DELETE'),
        confirmColor: 'danger',
        title: _('MODALS.EVENT_DELETION.ADMIN_DELETE_TITLE'),
        description: _('MODALS.EVENT_DELETION.ADMIN_DELETE_DESCRIPTION'),
        isAdmin: this.props.isAdmin || false,
        isEventOwner: this.props.isEventOwner || false
      }
    );
  }

  private async confirmReportWithReason(): Promise<ConfirmWithReasonModalResult> {
    return await this.modalService.presentAndGetResult(
      ConfirmWithReasonModalComponent,
      {
        cancelLabel:
          this.props.flagModalProps?.cancelLabel ?? _('COMMON.LABELS.CANCEL'),
        confirmLabel:
          this.props.flagModalProps?.confirmLabel ?? _('COMMON.LABELS.REPORT'),
        confirmColor: this.props.flagModalProps?.confirmColor ?? 'danger',
        title: this.props.flagModalProps?.title ?? _('COMMON.LABELS.REPORT'),
        description:
          this.props.flagModalProps?.description ??
          _('COMMON.LABELS.REPORT_DESCRIPTION')
      }
    );
  }

  private async confirmBlockWithReason(): Promise<ConfirmWithReasonModalResult> {
    return await this.modalService.presentAndGetResult(
      ConfirmWithReasonModalComponent,
      {
        cancelLabel:
          this.props?.blockModalProps?.cancelLabel ?? _('COMMON.LABELS.CANCEL'),
        confirmLabel:
          this.props?.blockModalProps?.confirmLabel ?? _('COMMON.LABELS.BLOCK'),
        confirmColor: this.props?.blockModalProps?.confirmColor ?? 'danger',
        title:
          this.props?.blockModalProps?.title ?? _('COMMON.LABELS.BLOCK_TITLE'),
        description:
          this.props?.blockModalProps?.description ??
          _('COMMON.LABELS.BLOCK_DESCRIPTION')
      }
    );
  }
}
